import { useEffect, useMemo, useRef } from 'react';
import ComponentWrapper from '../../fragments/component-wrapper/ComponentWrapper';

let giveButterScriptPromise = null;
let giveButterScriptSrc = null;

function loadGiveButterScript(scriptUrl) {
  if (!scriptUrl) return Promise.reject(new Error('Missing GiveButter script URL.'));

  if (giveButterScriptPromise && giveButterScriptSrc === scriptUrl) {
    return giveButterScriptPromise;
  }

  const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
  if (existingScript) {
    giveButterScriptSrc = scriptUrl;
    giveButterScriptPromise = new Promise((resolve, reject) => {
      if (existingScript.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load GiveButter script.')),
        { once: true }
      );
    });

    return giveButterScriptPromise;
  }

  giveButterScriptSrc = scriptUrl;
  giveButterScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load GiveButter script.'));
    document.body.appendChild(script);
  });

  return giveButterScriptPromise;
}

function extractCampaignIdentifierFromUrl(campaignUrl) {
  if (!campaignUrl) return '';

  try {
    const parsed = new URL(campaignUrl);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const campaignIndex = segments.findIndex((segment) => segment === 'c');
    if (campaignIndex > -1 && segments[campaignIndex + 1]) {
      return segments[campaignIndex + 1];
    }

    return segments[segments.length - 1] || '';
  } catch (error) {
    const fallback = campaignUrl.split('/').filter(Boolean);
    return fallback[fallback.length - 1] || '';
  }
}

function maybeInitGiveButterApiFallback({ campaignIdentifier, mountId }) {
  const globalObjects = [
    window?.GiveButterWidget,
    window?.GivebutterWidget,
    window?.GiveButter,
    window?.Givebutter,
  ].filter(Boolean);

  for (const candidate of globalObjects) {
    try {
      if (typeof candidate?.init === 'function') {
        candidate.init({ campaign: campaignIdentifier, container: `#${mountId}` });
        return true;
      }

      if (typeof candidate?.render === 'function') {
        candidate.render({ campaign: campaignIdentifier, container: `#${mountId}` });
        return true;
      }

      if (typeof candidate?.create === 'function') {
        candidate.create({ campaign: campaignIdentifier, container: `#${mountId}` });
        return true;
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('GiveButter fallback init failed:', error);
      }
    }
  }

  return false;
}

export default function GiveButterEmbed({ block }) {
  const widgetContainerRef = useRef(null);

  const scriptUrl = block?.scriptUrl || '';
  const campaignUrl = block?.campaignUrl || '';
  const mountId = block?.mountId || 'givebutter-widget';
  const isDirectEmbedUrl = /givebutter\.com\/embed\//i.test(campaignUrl);

  const campaignIdentifier = useMemo(() => {
    if (block?.campaignCodeOverride) return block.campaignCodeOverride;
    return extractCampaignIdentifierFromUrl(campaignUrl);
  }, [block?.campaignCodeOverride, campaignUrl]);

  useEffect(() => {
    if (!isDirectEmbedUrl || !scriptUrl) return;

    loadGiveButterScript(scriptUrl).catch((error) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('GiveButter script failed to load in embed mode:', error);
      }
    });
  }, [isDirectEmbedUrl, scriptUrl]);

  useEffect(() => {
    // Prefer true embed-only mode when a GiveButter embed URL is provided.
    // This avoids campaign-code resolution and matches the provided handoff.
    if (isDirectEmbedUrl) return;
    if (!scriptUrl || !campaignIdentifier) return;

    let cancelled = false;

    loadGiveButterScript(scriptUrl)
      .then(() => {
        if (cancelled || !widgetContainerRef.current) return;

        const widgetTagExists = widgetContainerRef.current.querySelector(
          'givebutter-giving-form'
        );

        if (!widgetTagExists) {
          const widgetElement = document.createElement('givebutter-giving-form');
          widgetElement.setAttribute('campaign', campaignIdentifier);
          widgetContainerRef.current.appendChild(widgetElement);
        }

        window.setTimeout(() => {
          if (cancelled || !widgetContainerRef.current) return;

          const wasWidgetRendered =
            widgetContainerRef.current.querySelector('iframe') ||
            widgetContainerRef.current.querySelector('givebutter-giving-form');

          if (!wasWidgetRendered) {
            maybeInitGiveButterApiFallback({ campaignIdentifier, mountId });
          }
        }, 250);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('GiveButter embed failed to initialise:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [campaignIdentifier, isDirectEmbedUrl, mountId, scriptUrl]);

  if (!campaignIdentifier && !isDirectEmbedUrl) return null;

  return (
    <ComponentWrapper className="BlockGiveButterEmbed" padding={false}>
      <div className="container">
        <div className="BlockGiveButterEmbed-inner">
          {isDirectEmbedUrl ? (
            <iframe
              src={campaignUrl}
              title="GiveButter Campaign"
              className="BlockGiveButterEmbed-widget BlockGiveButterEmbed-iframe"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <div
              id={mountId}
              ref={widgetContainerRef}
              className="BlockGiveButterEmbed-widget"
              data-campaign={campaignIdentifier}
            />
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
}
